/**
 * usePageAssignmentConfig — Fetch page assignment categories from backend (DRR-179)
 *
 * Backend-driven: categories, mode, constraints, and UI config come from
 * configs served via the pageAssignmentConfig GraphQL query.
 * Frontend NEVER defines categories — they come from the backend.
 *
 * Usage:
 *   const { config, loading } = usePageAssignmentConfig('floor_plans');
 *   if (config) return <DocumentPageAssigner config={config} ... />;
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ---------------------------------------------------------------------------
// GraphQL query
// ---------------------------------------------------------------------------

const PAGE_ASSIGNMENT_CONFIG_QUERY = gql`
  query GetPageAssignmentConfig($context: String!) {
    pageAssignmentConfig(context: $context) {
      contextId
      name
      description
      mode
      outputMime
      autoIncrement {
        enabled
        labelTemplate
        start
      }
      categories {
        id
        label
        description
        color
        icon
        sortOrder
      }
      constraints {
        multiCategory
        requiredCategories
        maxAssignments
      }
      ui {
        icon
        thumbnailWidth
        columns
        assignButtonLabel
        assignedBadgeColor
      }
    }
  }
`;

const PAGE_ASSIGNMENT_CONTEXTS_QUERY = gql`
  query GetPageAssignmentContexts {
    pageAssignmentContexts
  }
`;

// ---------------------------------------------------------------------------
// TypeScript types (mirror GraphQL schema)
// ---------------------------------------------------------------------------

export type PageAssignmentMode = 'IMAGE_EXPORT' | 'PAGE_RANGE';

export interface PageAssignmentCategory {
  id: string;
  label: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  sortOrder: number;
}

export interface PageAssignmentAutoIncrement {
  enabled: boolean;
  labelTemplate: string;
  start: number;
}

export interface PageAssignmentConstraints {
  multiCategory: boolean;
  requiredCategories: string[];
  maxAssignments: number;
}

export interface PageAssignmentUiConfig {
  icon: string;
  thumbnailWidth: number;
  columns: number;
  assignButtonLabel: string;
  assignedBadgeColor: string;
}

export interface PageAssignmentConfig {
  contextId: string;
  name: string;
  description: string;
  mode: PageAssignmentMode;
  outputMime: string | null;
  autoIncrement: PageAssignmentAutoIncrement | null;
  categories: PageAssignmentCategory[];
  constraints: PageAssignmentConstraints;
  ui: PageAssignmentUiConfig;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch page assignment config for a specific context.
 * Returns null if context not found.
 */
export function usePageAssignmentConfig(context: string) {
  const { data, loading, error } = useQuery<{
    pageAssignmentConfig: PageAssignmentConfig | null;
  }>(PAGE_ASSIGNMENT_CONFIG_QUERY, {
    variables: { context },
    fetchPolicy: 'cache-first',
  });

  return {
    config: data?.pageAssignmentConfig ?? null,
    loading,
    error,
  };
}

/**
 * Fetch all available page assignment context IDs.
 */
export function usePageAssignmentContexts() {
  const { data, loading, error } = useQuery<{
    pageAssignmentContexts: string[];
  }>(PAGE_ASSIGNMENT_CONTEXTS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  return {
    contexts: data?.pageAssignmentContexts ?? [],
    loading,
    error,
  };
}
